import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { localizeCountryName, resolveLocale } from '@/hooks/useCountryNames';
import type { ParentAccountOption } from './parentAccountOptions';

export type InheritedSettingsRow = { label: string; value: string };

/**
 * Spec 5.3, Step 2 in sub-account mode: what the sub-account inherits from its parent, as the
 * label/value rows the Settings and Review steps both render. Service permissions and Client
 * Accounts have no value of their own to show — they are copied from the parent on creation.
 */
export default function useInheritedSettingsRows(parentAccount: ParentAccountOption | null): InheritedSettingsRow[] {
  const { t } = useTranslation('translations', { keyPrefix: 'PartnerAccountCreation.Inherited' });
  const { t: tDetails } = useTranslation('translations', { keyPrefix: 'PartnerAccountCreation.Details' });
  const { t: tSettings } = useTranslation('translations', { keyPrefix: 'PartnerAccountCreation.Settings' });
  const { i18n } = useTranslation();

  // The book returns the parent's country as alpha-2, which Intl.DisplayNames localizes for any
  // country; the code itself is the fallback when CLDR has no name for it.
  const displayNames = useMemo(
    () => new Intl.DisplayNames([resolveLocale(i18n.language)], { type: 'region', fallback: 'none' }),
    [i18n.language],
  );

  if (!parentAccount) return [];

  const { country } = parentAccount;

  const sameAsParent = t('sameAsParent', {
    defaultValue: 'Same as {{parentName}}',
    parentName: parentAccount.accountName,
  });

  return [
    {
      label: tDetails('country', { defaultValue: 'Country' }),
      value: country && localizeCountryName({ name: country, alpha2: country, alpha3: country }, displayNames),
    },
    { label: t('currency', { defaultValue: 'Currency' }), value: parentAccount.currency },
    {
      label: tSettings('billingPackage', { defaultValue: 'Billing Package' }),
      value: parentAccount.billingPackageName,
    },
    { label: t('servicePermissions', { defaultValue: 'Service permissions' }), value: sameAsParent },
    { label: tSettings('clientAccounts', { defaultValue: 'Client Accounts' }), value: sameAsParent },
  ];
}
